import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import WizardLayout from "@/components/WizardLayout";
import SignaturePad from "@/components/SignaturePad";
import PhotoUploader from "@/components/PhotoUploader";
import CatalogPicker from "@/components/CatalogPicker";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { api, formatError } from "@/lib/api";
import { toast } from "sonner";
import { Wallet, ShieldCheck, Sparkles, Check as CheckIcon, PlayCircle, ExternalLink } from "lucide-react";

const VIDEOS_URL = "https://start.campervan.cat";

const STEPS = ["Datos", "Documentos", "Viaje", "Mejoras", "Firma", "Pago"];
const STEP_DATA = 0;
const STEP_DOCS = 1;
const STEP_TRIP = 2;
const STEP_UPGRADES = 3;
const STEP_SIGNATURE = 4;
const STEP_PAYMENT = 5;

function rentalDays(reservation) {
  if (!reservation) return 1;
  try {
    const d1 = new Date(reservation.pickup_date);
    const d2 = new Date(reservation.return_date);
    return Math.max(1, Math.round((d2 - d1) / (1000 * 60 * 60 * 24)));
  } catch {
    return 1;
  }
}

export default function ClientPreCheckin() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [reservation, setReservation] = useState(null);
  const [products, setProducts] = useState([]);
  const [selection, setSelection] = useState({}); // { product_id: qty }
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [checkinSubmitted, setCheckinSubmitted] = useState(false);
  const [ocrBusy, setOcrBusy] = useState(false);
  const [ocrSuggestion, setOcrSuggestion] = useState(null); // {fields, source}
  const [data, setData] = useState({
    full_name: "",
    dni_number: "",
    license_number: "",
    license_issue_date: "",
    birth_date: "",
    address: "",
    phone: "",
    email: "",
    countries_to_visit: "",
    dni_photo_front: "",
    dni_photo_back: "",
    license_photo: "",
    km_package: "100",
    additional_driver_requested: false,
    signature: "",
    accept_terms: false,
    whatsapp_opt_in: false,
  });
  // Segundo conductor (sin coste): datos + documentación en el pre-checkin
  const [driver, setDriver] = useState({
    full_name: "",
    dni_number: "",
    license_number: "",
    license_issue_date: "",
    birth_date: "",
    dni_photo_front: "",
    dni_photo_back: "",
    license_photo: "",
  });
  const setDrv = (k, v) => setDriver((d) => ({ ...d, [k]: v }));

  const reload = async () => {
    const res = await api.get(`/r/${code}`);
    setReservation(res.data);
    return res.data;
  };

  useEffect(() => {
    Promise.all([api.get(`/r/${code}`), api.get(`/catalog`)])
      .then(([resR, resC]) => {
        const r = resR.data;
        setReservation(r);
        const catalogProducts = resC.data.products || [];
        setProducts(catalogProducts);
        setData((d) => ({
          ...d,
          full_name: r.client_name || "",
          phone: r.client_phone || "",
          email: r.client_email || "",
          km_package: r.km_package || "100",
          additional_driver_requested: Boolean(r.additional_driver_requested),
        }));
        // Rebuild the picker state from the stored snapshot, or preselect
        // catalog defaults (base insurance + included km)
        const initial = {};
        if ((r.selected_items || []).length > 0) {
          r.selected_items.forEach((it) => {
            initial[it.product_id] = it.qty;
          });
        } else {
          catalogProducts
            .filter((p) => p.meta?.is_default)
            .forEach((p) => {
              initial[p.id] = 1;
            });
        }
        setSelection(initial);
        if (r.has_checkin) {
          setCheckinSubmitted(true);
          setStep(STEP_PAYMENT);
        }
      })
      .catch((err) => {
        toast.error(formatError(err));
        navigate("/");
      });
  }, [code, navigate]);

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));
  const days = rentalDays(reservation);
  const driverProduct = products.find((p) => p.meta?.is_additional_driver);
  const driverSelected = Boolean(driverProduct && selection[driverProduct.id] > 0);

  // OCR para el 2º conductor: autorrellena directamente los campos vacíos
  const runDriverOcr = async (rawImage, docType) => {
    try {
      const { data: resp } = await api.post(`/r/${code}/ocr`, {
        image: rawImage,
        doc_type: docType,
      });
      if (resp.ok && resp.fields) {
        setDriver((d) => {
          const next = { ...d };
          Object.entries(resp.fields).forEach(([k, v]) => {
            if (k in next && v && !next[k]) next[k] = v;
          });
          return next;
        });
        if (Object.values(resp.fields).some(Boolean)) {
          toast.success("Datos del 2º conductor leídos del documento");
        }
      }
    } catch {
      // best-effort
    }
  };

  // ---------- OCR autofill ----------
  const runOcr = async (rawImage, docType) => {
    setOcrBusy(true);
    try {
      const { data: resp } = await api.post(`/r/${code}/ocr`, {
        image: rawImage,
        doc_type: docType,
      });
      if (resp.ok && resp.fields) {
        const nonEmpty = Object.fromEntries(
          Object.entries(resp.fields).filter(([, v]) => v)
        );
        if (Object.keys(nonEmpty).length > 0) {
          setOcrSuggestion({ fields: nonEmpty, source: docType });
        }
      }
    } catch {
      // OCR is best-effort: the client can always type the data
    } finally {
      setOcrBusy(false);
    }
  };

  const applyOcr = () => {
    if (!ocrSuggestion) return;
    setData((d) => {
      const next = { ...d };
      Object.entries(ocrSuggestion.fields).forEach(([k, v]) => {
        if (k in next && v) next[k] = v;
      });
      return next;
    });
    setOcrSuggestion(null);
    toast.success("Datos aplicados. Revísalos en el paso 1.");
  };

  // ---------- Steps ----------
  const isStepValid = () => {
    switch (step) {
      case STEP_DATA:
        return (
          data.full_name && data.dni_number && data.license_number &&
          data.birth_date && data.address && data.phone
        );
      case STEP_DOCS:
        return data.dni_photo_front && data.dni_photo_back && data.license_photo;
      case STEP_TRIP:
        return true;
      case STEP_UPGRADES:
        // Con 2º conductor seleccionado, exigimos sus datos y documentación
        if (driverSelected) {
          return Boolean(
            driver.full_name && driver.dni_number && driver.license_number &&
            driver.dni_photo_front && driver.dni_photo_back && driver.license_photo
          );
        }
        return true;
      case STEP_SIGNATURE:
        return data.signature && data.accept_terms;
      case STEP_PAYMENT:
        return true;
      default:
        return false;
    }
  };

  const saveSelection = async () => {
    const items = Object.entries(selection)
      .filter(([, qty]) => qty > 0)
      .map(([product_id, qty]) => ({ product_id, qty }));
    const { data: resp } = await api.post(`/r/${code}/items`, { items });
    await reload();
    return resp;
  };

  const submitCheckin = async () => {
    setLoading(true);
    try {
      const resp = await api.post(`/r/${code}/checkin`, {
        ...data,
        additional_driver_requested: driverSelected,
        additional_driver: driverSelected ? driver : null,
      });
      await reload();
      setCheckinSubmitted(true);
      toast.success("Pre-Checkin registrado");
      setStep(STEP_PAYMENT);
      return resp.data;
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setLoading(false);
    }
  };

  const startPayment = async () => {
    setLoading(true);
    try {
      const { data: resp } = await api.post(`/payments/checkout/session`, {
        reservation_code: code,
        origin_url: window.location.origin,
      });
      window.location.href = resp.url;
    } catch (err) {
      toast.error(formatError(err));
      setLoading(false);
    }
  };

  const startSignalPayment = async () => {
    setLoading(true);
    try {
      const { data: resp } = await api.post(`/payments/signal/session`, {
        reservation_code: code,
        origin_url: window.location.origin,
      });
      window.location.href = resp.url;
    } catch (err) {
      toast.error(formatError(err));
      setLoading(false);
    }
  };

  const startExtrasPayment = async () => {
    setLoading(true);
    try {
      const { data: resp } = await api.post(`/payments/extras/session`, {
        reservation_code: code,
        origin_url: window.location.origin,
      });
      window.location.href = resp.url;
    } catch (err) {
      toast.error(formatError(err));
      setLoading(false);
    }
  };

  const startRentalPayment = async () => {
    setLoading(true);
    try {
      const { data: resp } = await api.post(`/payments/rental/session`, {
        reservation_code: code,
        origin_url: window.location.origin,
      });
      window.location.href = resp.url;
    } catch (err) {
      toast.error(formatError(err));
      setLoading(false);
    }
  };

  const onNext = async () => {
    if (step === STEP_UPGRADES) {
      setLoading(true);
      try {
        await saveSelection();
        setStep(STEP_SIGNATURE);
      } catch (err) {
        toast.error(formatError(err));
      } finally {
        setLoading(false);
      }
      return;
    }
    if (step === STEP_SIGNATURE) {
      await submitCheckin();
      return;
    }
    if (step === STEP_PAYMENT) {
      // Recogida lejana con señal sin pagar: primero la señal, y nada más.
      // El resto y la fianza se piden por correo antes de la entrega.
      if (reservation?.payment_plan?.stage === "signal") {
        await startSignalPayment();
        return;
      }
      const depositOk =
        reservation?.payment_status === "paid" || reservation?.payment_status === "on_hold";
      if (!depositOk) {
        await startPayment();
        return;
      }
      const rentalPendingNow =
        (reservation?.total_amount || 0) > 0 && reservation?.rental_payment_status === "pending";
      if (rentalPendingNow) {
        await startRentalPayment();
        return;
      }
      const extrasPending =
        (reservation?.extras_amount || 0) > 0 && reservation?.extras_payment_status !== "paid";
      if (extrasPending) {
        await startExtrasPayment();
        return;
      }
      navigate(`/reserva/${code}`);
      return;
    }
    setStep((s) => Math.min(STEPS.length - 1, s + 1));
  };

  const onBack = () => {
    if (step === 0) {
      navigate(`/reserva/${code}`);
      return;
    }
    if (checkinSubmitted) return;
    setStep((s) => Math.max(0, s - 1));
  };

  if (!reservation) return null;

  const depositLocked =
    reservation.payment_status === "paid" || reservation.payment_status === "on_hold";
  const extrasAmount = Number(reservation.extras_amount || 0);
  const extrasPaid = reservation.extras_payment_status === "paid";
  const extrasPending = extrasAmount > 0 && !extrasPaid;
  const rentalAmount = Number(reservation.total_amount || 0);
  const rentalPending = rentalAmount > 0 && reservation.rental_payment_status === "pending";
  // Plan de cobro del backend (payment_plan.py): qué toca pagar ahora.
  const plan = reservation.payment_plan || {};
  const signalStage = plan.stage === "signal";
  const signalPaid = reservation.signal_payment_status === "paid";
  const rentalDue = Number(plan.balance_amount ?? rentalAmount);
  const rentalPaid = rentalAmount > 0 && reservation.rental_payment_status === "paid";
  const selectedSnapshot = reservation.selected_items || [];

  const stepTitles = {
    [STEP_DATA]: "Tus datos",
    [STEP_DOCS]: "Documentación",
    [STEP_TRIP]: "Tu viaje",
    [STEP_UPGRADES]: "Mejora tu viaje",
    [STEP_SIGNATURE]: "Firma del contrato",
    [STEP_PAYMENT]: "Pago",
  };

  const nextLabel = (() => {
    if (step === STEP_UPGRADES) return "Guardar y continuar";
    if (step === STEP_SIGNATURE) return "Confirmar y firmar";
    if (step === STEP_PAYMENT) {
      if (signalStage) return `Pagar señal ${Number(plan.signal_amount).toFixed(2)} €`;
      if (!depositLocked) {
        return rentalPending
          ? (signalPaid ? "Pagar resto y fianza" : "Pagar alquiler y fianza")
          : `Pagar fianza ${reservation.deposit_amount} €`;
      }
      if (rentalPending) return `Pagar ${signalPaid ? "resto" : "alquiler"} ${rentalDue.toFixed(2)} €`;
      if (extrasPending) return `Pagar extras ${extrasAmount.toFixed(2)} €`;
      return "Volver a mi reserva";
    }
    return "Siguiente";
  })();

  return (
    <WizardLayout
      title={stepTitles[step] || ""}
      step={step}
      totalSteps={STEPS.length}
      stepLabel={`Pre-Checkin · ${STEPS[step]}`}
      onBack={onBack}
      onNext={onNext}
      nextLabel={nextLabel}
      nextDisabled={!isStepValid()}
      isLastStep={false}
      loading={loading}
    >
      {step === STEP_DATA && (
        <div className="space-y-4 max-w-xl">
          <p className="text-warmgray">
            Necesitamos tus datos personales para preparar el contrato de
            alquiler antes de la entrega del vehículo.
          </p>
          <div>
            <Label>Nombre y apellidos</Label>
            <Input value={data.full_name} onChange={(e) => set("full_name", e.target.value)} className="mt-2 bg-white border-sand" data-testid="ci-full-name" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>DNI / NIE</Label>
              <Input value={data.dni_number} onChange={(e) => set("dni_number", e.target.value)} className="mt-2 bg-white border-sand" data-testid="ci-dni" />
            </div>
            <div>
              <Label>Fecha de nacimiento</Label>
              <Input type="date" value={data.birth_date} onChange={(e) => set("birth_date", e.target.value)} className="mt-2 bg-white border-sand" data-testid="ci-birth" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Número de carnet</Label>
              <Input value={data.license_number} onChange={(e) => set("license_number", e.target.value)} className="mt-2 bg-white border-sand" data-testid="ci-license" />
            </div>
            <div>
              <Label>Carnet obtenido el</Label>
              <Input type="date" value={data.license_issue_date} onChange={(e) => set("license_issue_date", e.target.value)} className="mt-2 bg-white border-sand" data-testid="ci-license-issue" />
            </div>
          </div>
          <div>
            <Label>Dirección</Label>
            <Input value={data.address} onChange={(e) => set("address", e.target.value)} className="mt-2 bg-white border-sand" data-testid="ci-address" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Teléfono</Label>
              <Input value={data.phone} onChange={(e) => set("phone", e.target.value)} className="mt-2 bg-white border-sand" data-testid="ci-phone" />
            </div>
            <div>
              <Label>Email</Label>
              <Input type="email" value={data.email} onChange={(e) => set("email", e.target.value)} className="mt-2 bg-white border-sand" data-testid="ci-email" />
            </div>
          </div>
        </div>
      )}

      {step === STEP_DOCS && (
        <div className="space-y-5 max-w-xl">
          <p className="text-warmgray">
            Sube fotos claras de tu DNI (ambas caras) y tu carnet de conducir.
            Leeremos los datos automáticamente para ahorrarte teclear.
          </p>
          {ocrBusy && (
            <div className="bg-forest-deep/5 border border-forest-deep/20 rounded-xl p-3 text-sm text-forest-deep flex items-center gap-2" data-testid="ci-ocr-busy">
              <Sparkles className="h-4 w-4 animate-pulse" /> Leyendo documento...
            </div>
          )}
          {ocrSuggestion && (
            <div className="bg-warn/10 border border-warn/30 rounded-xl p-4" data-testid="ci-ocr-suggestion">
              <div className="text-sm font-semibold text-warn-ink mb-2 flex items-center gap-2">
                <Sparkles className="h-4 w-4" /> Datos detectados en el documento
              </div>
              <ul className="text-sm text-warmgray space-y-0.5 mb-3">
                {Object.entries(ocrSuggestion.fields).map(([k, v]) => (
                  <li key={k}><span className="text-warmgray-light">{k}:</span> <strong className="text-charcoal">{v}</strong></li>
                ))}
              </ul>
              <div className="flex gap-2">
                <Button size="sm" onClick={applyOcr} className="bg-forest-deep hover:bg-forest text-white" data-testid="ci-ocr-apply">
                  Aplicar a mis datos
                </Button>
                <Button size="sm" variant="outline" onClick={() => setOcrSuggestion(null)} className="border-sand">
                  Descartar
                </Button>
              </div>
            </div>
          )}
          <PhotoUploader
            label="DNI / NIE — Anverso"
            value={data.dni_photo_front}
            onChange={(v) => set("dni_photo_front", v)}
            uploadCode={code}
            onRaw={(raw) => runOcr(raw, "dni_front")}
            testid="ci-photo-dni-front"
          />
          <PhotoUploader
            label="DNI / NIE — Reverso"
            value={data.dni_photo_back}
            onChange={(v) => set("dni_photo_back", v)}
            uploadCode={code}
            onRaw={(raw) => runOcr(raw, "dni_back")}
            testid="ci-photo-dni-back"
          />
          <PhotoUploader
            label="Carnet de conducir"
            value={data.license_photo}
            onChange={(v) => set("license_photo", v)}
            uploadCode={code}
            onRaw={(raw) => runOcr(raw, "license")}
            testid="ci-photo-license"
          />
        </div>
      )}

      {step === STEP_TRIP && (
        <div className="space-y-4 max-w-xl">
          <p className="text-warmgray">
            Indícanos por dónde tienes previsto viajar. España continental
            está incluida por defecto.
          </p>
          <div>
            <Label>Países a visitar</Label>
            <Textarea value={data.countries_to_visit} onChange={(e) => set("countries_to_visit", e.target.value)} className="mt-2 bg-white border-sand" rows={3} placeholder="Ej. España, Francia, Portugal" data-testid="ci-countries" />
          </div>
        </div>
      )}

      {step === STEP_UPGRADES && (
        <div className="max-w-xl" data-testid="ci-upgrades-step">
          <p className="text-warmgray mb-5">
            Personaliza tu viaje: cobertura, kilómetros y extras. Todo se
            cobra junto a la fianza en un único pago seguro con Stripe.
          </p>
          <CatalogPicker
            products={products}
            days={days}
            value={selection}
            onChange={setSelection}
          />

          {driverSelected && (
            <div className="mt-6 bg-white border-2 border-forest-deep rounded-xl p-5 space-y-4" data-testid="ci-driver-form">
              <div>
                <h4 className="font-display font-bold text-charcoal">Segundo conductor</h4>
                <p className="text-sm text-warmgray mt-0.5">
                  Sin coste. Necesitamos sus datos y documentación para
                  incluirlo en el contrato y el seguro.
                </p>
              </div>
              <div>
                <Label>Nombre y apellidos</Label>
                <Input value={driver.full_name} onChange={(e) => setDrv("full_name", e.target.value)} className="mt-2 bg-white border-sand" data-testid="ci-drv-name" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>DNI / NIE</Label>
                  <Input value={driver.dni_number} onChange={(e) => setDrv("dni_number", e.target.value)} className="mt-2 bg-white border-sand" data-testid="ci-drv-dni" />
                </div>
                <div>
                  <Label>Fecha de nacimiento</Label>
                  <Input type="date" value={driver.birth_date} onChange={(e) => setDrv("birth_date", e.target.value)} className="mt-2 bg-white border-sand" data-testid="ci-drv-birth" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Número de carnet</Label>
                  <Input value={driver.license_number} onChange={(e) => setDrv("license_number", e.target.value)} className="mt-2 bg-white border-sand" data-testid="ci-drv-license" />
                </div>
                <div>
                  <Label>Carnet obtenido el</Label>
                  <Input type="date" value={driver.license_issue_date} onChange={(e) => setDrv("license_issue_date", e.target.value)} className="mt-2 bg-white border-sand" data-testid="ci-drv-issue" />
                </div>
              </div>
              <PhotoUploader
                label="DNI / NIE del 2º conductor — Anverso"
                value={driver.dni_photo_front}
                onChange={(v) => setDrv("dni_photo_front", v)}
                uploadCode={code}
                onRaw={(raw) => runDriverOcr(raw, "dni_front")}
                testid="ci-drv-dni-front"
              />
              <PhotoUploader
                label="DNI / NIE del 2º conductor — Reverso"
                value={driver.dni_photo_back}
                onChange={(v) => setDrv("dni_photo_back", v)}
                uploadCode={code}
                onRaw={(raw) => runDriverOcr(raw, "dni_back")}
                testid="ci-drv-dni-back"
              />
              <PhotoUploader
                label="Carnet de conducir del 2º conductor"
                value={driver.license_photo}
                onChange={(v) => setDrv("license_photo", v)}
                uploadCode={code}
                onRaw={(raw) => runDriverOcr(raw, "license")}
                testid="ci-drv-license"
              />
            </div>
          )}
        </div>
      )}

      {step === STEP_SIGNATURE && (
        <div className="space-y-5 max-w-xl">
          <div className="bg-white border border-sand rounded-xl p-5 text-sm text-warmgray leading-relaxed max-h-72 overflow-y-auto">
            <h4 className="font-display font-bold text-charcoal mb-2">Resumen del contrato</h4>
            <p className="mb-2">
              <strong>{data.full_name}</strong> (DNI {data.dni_number}) reconoce
              haber recibido la información del estado de la furgoneta
              Volkswagen California Ocean y la inspeccionará presencialmente
              con el propietario el día de la entrega.
            </p>
            <p className="mb-2">
              Periodo de alquiler: <strong>{reservation.pickup_date} {reservation.pickup_time}</strong>
              {" → "}<strong>{reservation.return_date} {reservation.return_time}</strong>.
              Lugar de devolución: {reservation.return_location}.
            </p>
            {selectedSnapshot.length > 0 && (
              <div className="mb-2">
                Mejoras y extras contratados:
                <ul className="list-disc pl-5 mt-1">
                  {selectedSnapshot.map((it) => (
                    <li key={it.product_id}>
                      {it.name}{it.qty > 1 ? ` ×${it.qty}` : ""}
                      {it.subtotal > 0 ? ` — ${it.subtotal.toFixed(2)} €` : " — incluido"}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {driverSelected && driver.full_name && (
              <p className="mb-2">
                Segundo conductor autorizado (sin coste): <strong>{driver.full_name}</strong> (DNI {driver.dni_number}).
              </p>
            )}
            <p className="mb-2">
              Importe total alquiler: <strong>{Number(reservation.total_amount || 0).toFixed(2)} €</strong>.
              {extrasAmount > 0 && <> Extras: <strong>{extrasAmount.toFixed(2)} €</strong>.</>}
              {" "}Fianza: <strong>{reservation.deposit_amount} €</strong>.
            </p>
            <p>
              El viajero se compromete a devolver el vehículo en su estado
              original (limpieza, depósitos vaciados/llenados, combustible
              equivalente al de salida) y reconoce las coberturas del seguro y
              franquicias. Autoriza el uso de la tarjeta de la fianza para
              cargos posteriores justificados (exceso de km, daños o extras
              pendientes) conforme al contrato.
            </p>
          </div>
          <label className="flex items-start gap-3 cursor-pointer">
            <Checkbox checked={data.accept_terms} onCheckedChange={(v) => set("accept_terms", Boolean(v))} data-testid="ci-accept-terms" />
            <span className="text-sm text-charcoal">
              He leído y acepto las condiciones del contrato de alquiler.
            </span>
          </label>
          <label className="flex items-start gap-3 cursor-pointer">
            <Checkbox checked={data.whatsapp_opt_in} onCheckedChange={(v) => set("whatsapp_opt_in", Boolean(v))} data-testid="ci-whatsapp-optin" />
            <span className="text-sm text-charcoal">
              Quiero recibir los avisos de mi reserva también por WhatsApp (opcional).
            </span>
          </label>
          <div>
            <Label>Firma</Label>
            <div className="mt-2">
              <SignaturePad value={data.signature} onChange={(v) => set("signature", v)} testid="ci-signature" />
            </div>
          </div>
        </div>
      )}

      {step === STEP_PAYMENT && (
        <div className="max-w-xl space-y-5" data-testid="ci-payment-step">
          {signalStage ? (
            <div className="bg-white border border-sand rounded-xl p-6" data-testid="ci-signal-card">
              <div className="flex items-start gap-4">
                <div className="h-12 w-12 rounded-xl bg-forest-deep text-white flex items-center justify-center shrink-0">
                  <Wallet className="h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="font-display text-xl font-bold text-charcoal">Señal para confirmar la reserva</h3>
                  <div className="mt-3 space-y-1.5 text-sm">
                    <div className="flex justify-between">
                      <span className="text-warmgray">Señal ({Number(plan.signal_percent).toFixed(0)} % del alquiler)</span>
                      <strong>{Number(plan.signal_amount).toFixed(2)} €</strong>
                    </div>
                    <div className="flex justify-between text-warmgray-light">
                      <span>Resto del alquiler · antes de la entrega</span>
                      <span>{Number(plan.balance_amount).toFixed(2)} €</span>
                    </div>
                    <div className="flex justify-between text-warmgray-light">
                      <span>Fianza (retención, no cobro) · antes de la entrega</span>
                      <span>{Number(reservation.deposit_amount).toFixed(0)} €</span>
                    </div>
                  </div>
                  <p className="text-xs text-warmgray-light mt-3">
                    Pago seguro con Stripe. Ahora solo se cobra la señal. Unos días
                    antes de la recogida te enviaremos un correo para pagar el resto
                    y dejar la fianza retenida en tu tarjeta.
                  </p>
                </div>
              </div>
            </div>
          ) : depositLocked ? (
            <div className="bg-ok/10 border border-ok/30 rounded-xl p-6 text-center">
              <ShieldCheck className="h-10 w-10 text-ok mx-auto mb-3" />
              <h3 className="font-display text-xl font-bold text-charcoal mb-1">
                ¡Fianza confirmada!
              </h3>
              <p className="text-sm text-warmgray">
                Tu fianza de {reservation.deposit_amount} € está {reservation.payment_status === "on_hold" ? "preautorizada (sin cargo real)" : "retenida"} y se
                liberará tras la devolución sin incidencias.
              </p>
            </div>
          ) : (
            <div className="bg-white border border-sand rounded-xl p-6">
              <div className="flex items-start gap-4">
                <div className="h-12 w-12 rounded-xl bg-terracotta text-white flex items-center justify-center shrink-0">
                  <Wallet className="h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="font-display text-xl font-bold text-charcoal">Un único pago</h3>
                  <div className="mt-3 space-y-1.5 text-sm">
                    {rentalPending && (
                      <div className="flex justify-between" data-testid="ci-pay-rental-line">
                        <span className="text-warmgray">{signalPaid ? "Resto del alquiler" : "Alquiler"}</span>
                        <strong>{rentalDue.toFixed(2)} €</strong>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-warmgray">Fianza (reembolsable)</span>
                      <strong>{Number(reservation.deposit_amount).toFixed(2)} €</strong>
                    </div>
                    {extrasAmount > 0 && (
                      <div className="flex justify-between">
                        <span className="text-warmgray">Mejoras y extras</span>
                        <strong>{extrasAmount.toFixed(2)} €</strong>
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-warmgray-light mt-3">
                    Pago seguro con Stripe. Según las fechas, la fianza se
                    preautoriza (sin cargo real) o se cobra y se devuelve tras
                    la inspección; {rentalPending ? "el alquiler y los extras se cobran" : "los extras se cobran"} con
                    la misma tarjeta.
                  </p>
                </div>
              </div>
            </div>
          )}

          {checkinSubmitted && (
            <a
              href={VIDEOS_URL}
              target="_blank"
              rel="noreferrer"
              className="block bg-forest-deep hover:bg-forest transition-colors rounded-xl p-5 text-white"
              data-testid="ci-videos-card"
            >
              <div className="flex items-center gap-4">
                <div className="h-12 w-12 rounded-xl bg-white/15 flex items-center justify-center shrink-0">
                  <PlayCircle className="h-6 w-6" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-display font-bold text-lg">
                    Aprende a usar tu camper antes de la recogida
                  </div>
                  <div className="text-sm text-white/80 mt-0.5">
                    Vídeos cortos: techo elevable, cocina, aguas, calefacción,
                    modo camping... Todo en start.campervan.cat
                  </div>
                </div>
                <ExternalLink className="h-5 w-5 shrink-0 text-white/70" />
              </div>
            </a>
          )}

          {depositLocked && rentalPending && (
            <div className="bg-warn/10 border border-warn/30 rounded-xl p-4" data-testid="ci-rental-pending">
              <div className="text-sm font-semibold text-warn-ink mb-1">
                Alquiler pendiente: {rentalAmount.toFixed(2)} €
              </div>
              <p className="text-xs text-warn-ink">
                Pulsa el botón para completar el pago del alquiler de forma
                segura con Stripe.
              </p>
            </div>
          )}
          {depositLocked && rentalPaid && (
            <div className="bg-ok/10 border border-ok/30 rounded-xl p-4 flex items-center gap-3" data-testid="ci-rental-ok">
              <CheckIcon className="h-5 w-5 text-ok" />
              <div className="text-sm text-charcoal">Alquiler pagado: {rentalAmount.toFixed(2)} €</div>
            </div>
          )}
          {depositLocked && (
            extrasPaid && extrasAmount > 0 ? (
              <div className="bg-ok/10 border border-ok/30 rounded-xl p-4 flex items-center gap-3" data-testid="ci-extras-ok">
                <CheckIcon className="h-5 w-5 text-ok" />
                <div className="text-sm text-charcoal">Extras pagados: {extrasAmount.toFixed(2)} €. ¡Todo listo para la entrega!</div>
              </div>
            ) : extrasPending ? (
              <div className="bg-warn/10 border border-warn/30 rounded-xl p-4" data-testid="ci-extras-pending">
                <div className="text-sm font-semibold text-warn-ink mb-1">Extras pendientes: {extrasAmount.toFixed(2)} €</div>
                <p className="text-xs text-warn-ink">
                  No se pudieron cobrar automáticamente. Pulsa el botón para
                  completar el pago con Stripe.
                </p>
              </div>
            ) : null
          )}
        </div>
      )}
    </WizardLayout>
  );
}
